import { getSearchResultsCustomFontProperties } from 'client/components/core/Sidebar/components/Settings/entities/search-results-settings/utils/create-search-results'
import { getContentBoxCustomFontProperties } from 'client/components/entities/ContentBox/utils/getContentBoxFontProperties'
import { getContentTableCustomFontProperties } from 'client/components/entities/ContentTable'
import { getFaqCustomFontProperties } from 'client/components/entities/Faq'
import { getSurveyCustomFontProperties } from 'client/components/entities/Survey/Survey'
import { getNewBlogPostListingCustomFontProperties } from 'client/pages/blog-page/entities/BlogPostListing'
import { getNewButtonCustomFontProperties } from 'client/pages/contact-us/entities/contact-us-button'
import { getOfferPriceCustomFontProperties } from 'client/pages/offer/entities/OfferPrice'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { BlogPostListingInterface } from 'common/types/entities/BlogPostListingInterface'
import { ButtonInterface } from 'common/types/entities/button-interface'
import { ContentBoxInterface } from 'common/types/entities/ContentBoxInterface'
import { ContentTableInterface } from 'common/types/entities/ContentTableInterface'
import { FaqInterface } from 'src/shared/entities/faq/faq.schema'
import { LatestBlogPostsInterface } from 'common/types/entities/LatestBlogPostsInterface'
import { OfferPriceInterface } from 'common/types/entities/OfferPriceInterface'
import { SurveyInterface } from 'common/types/entities/SurveyInterface'
import { SearchResultsInterface } from 'common/types/entities/search-results-interface'
import { GlobalSettingsInterface } from 'common/types/Page'
import { CustomFontProperties } from 'common/utils/fontsUtils'
import { getSearchCustomFontProperties } from 'src/editor/shared/entities/search/create-search'
import { DataFilesDto } from 'src/server/dto/data-file.dto'
import {
  BaseTextContentEntityInterface,
  PageEntityInterface,
} from '../types/base-entity-interface'
import { FormInterface } from '../types/entities/form-interface'
import { getFormCustomFontProperties } from 'src/editor/shared/entities/form/get-form-custom-font-properties'
import { SearchInterface } from '../types/entities/search-interface'

export class CustomFontsGenerator {
  public generate(
    entities: PageEntityInterface[],
    files: DataFilesDto,
    globalFontsProperties: CustomFontProperties[] = [],
  ) {
    const entitiesFontsProperties =
      this.getEntitiesCustomFontsProperties(entities)
    const pageCustomFonts = [
      ...entitiesFontsProperties,
      ...globalFontsProperties,
    ]

    return this.generateCustomFontFaces(pageCustomFonts, files)
  }

  private getEntitiesCustomFontsProperties(entities: PageEntityInterface[]) {
    return entities.flatMap(entity => this.getCustomFontProperties(entity))
  }

  private getCustomFontProperties(
    entity: PageEntityInterface,
  ): CustomFontProperties[] {
    switch (entity.type) {
      case EntityTypeEnum.ContentTable:
        return getContentTableCustomFontProperties(
          entity as ContentTableInterface,
        )
      case EntityTypeEnum.ContentBox:
        return getContentBoxCustomFontProperties(entity as ContentBoxInterface)
      case EntityTypeEnum.Faq:
        return getFaqCustomFontProperties(entity as FaqInterface)
      case EntityTypeEnum.OfferPrice:
        return getOfferPriceCustomFontProperties(entity as OfferPriceInterface)
      case EntityTypeEnum.Button:
      case EntityTypeEnum.PaymentButton:
      case EntityTypeEnum.UpsellAgreeButton:
      case EntityTypeEnum.UpsellDisagreeButton:
      case EntityTypeEnum.WebinarCallToAction:
      case EntityTypeEnum.SalesRedirectionButton:
        return getNewButtonCustomFontProperties(entity as ButtonInterface)
      case EntityTypeEnum.Survey:
        return getSurveyCustomFontProperties(entity as SurveyInterface)
      case EntityTypeEnum.BlogPostListing:
      case EntityTypeEnum.LatestBlogPosts:
        return getNewBlogPostListingCustomFontProperties(
          entity as BlogPostListingInterface | LatestBlogPostsInterface,
        )
      case EntityTypeEnum.SearchResults:
        return getSearchResultsCustomFontProperties(
          entity as SearchResultsInterface,
        )
      case EntityTypeEnum.Search:
        return getSearchCustomFontProperties(entity as SearchInterface)
      case EntityTypeEnum.Form:
        return getFormCustomFontProperties(entity as FormInterface)
      default:
        return CustomFontsGenerator.getBaseCustomFontProperties(
          entity as BaseTextContentEntityInterface,
        )
    }
  }

  private generateCustomFontFaces(
    fonts: CustomFontProperties[],
    files: DataFilesDto,
  ) {
    return fonts
      .map(font => {
        const fontFile = files.find(file => font.fontFileId === file.id)
        return fontFile
          ? `@font-face {
            font-family: ${font.fontFamily};
            src: url('${fontFile.path}');
          }`
          : ''
      })
      .join('\n')
      .trim()
  }

  static getBaseCustomFontProperties(
    entity: Pick<
      BaseTextContentEntityInterface,
      'fontFamily' | 'fontFileId' | 'mobileFontFamily' | 'mobileFontFileId'
    >,
  ) {
    const fontProperties = []

    if (entity.fontFamily && entity.fontFileId) {
      fontProperties.push({
        fontFamily: entity.fontFamily,
        fontFileId: entity.fontFileId,
      })
    }

    if (entity.mobileFontFamily && entity.mobileFontFileId) {
      fontProperties.push({
        fontFamily: entity.mobileFontFamily,
        fontFileId: entity.mobileFontFileId,
      })
    }

    return fontProperties
  }
}

export class PageCustomFontsGenerator extends CustomFontsGenerator {
  public generateFonts(
    entities: PageEntityInterface[],
    files: DataFilesDto,
    globalSettings: GlobalSettingsInterface,
  ) {
    const globalSettingsFonts =
      this.getGlobalSettingsCustomFontProperties(globalSettings)
    return super.generate(entities, files, globalSettingsFonts)
  }

  private getGlobalSettingsCustomFontProperties = (
    globalSettings: GlobalSettingsInterface = {},
  ): CustomFontProperties[] => {
    const fontProperties: CustomFontProperties[] = []

    if (globalSettings.fontFamily && globalSettings.fontFileId) {
      fontProperties.push({
        fontFamily: globalSettings.fontFamily,
        fontFileId: globalSettings.fontFileId,
      })
    }

    if (globalSettings.headingFontFamily && globalSettings.headingFontFileId) {
      fontProperties.push({
        fontFamily: globalSettings.headingFontFamily,
        fontFileId: globalSettings.headingFontFileId,
      })
    }

    return fontProperties
  }
}

export class WebsiteCustomFontsGenerator extends CustomFontsGenerator {
  public generateFonts(
    entities: PageEntityInterface[],
    files: DataFilesDto,
    websiteStylesTypographies: Partial<CustomFontProperties>[],
  ) {
    const websiteStylesFonts = this.getWebsiteStyleCustomFontProperties(
      websiteStylesTypographies,
    )
    return super.generate(entities, files, websiteStylesFonts)
  }

  private getWebsiteStyleCustomFontProperties = (
    websiteStylesTypographies: Partial<CustomFontProperties>[] = [],
  ): CustomFontProperties[] => {
    const fontProperties: CustomFontProperties[] = []

    websiteStylesTypographies.forEach(typography => {
      if (typography.fontFamily && typography.fontFileId) {
        fontProperties.push({
          fontFamily: typography.fontFamily,
          fontFileId: typography.fontFileId,
        })
      }
    })

    return fontProperties
  }
}
